import { useEffect, useState } from "react";
import { ptBR } from "@mui/x-data-grid/locales";
import { Alert, Snackbar } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ChartDataProps, graficType } from "../../graficos";
import { generateRandomColor } from "../../../core";
import { generateTableCols, generateTableData, generateTableGroups } from "./generate_table_data";


export interface TableProps {
  onDataChange?: (data: { [key: string]: number | string; }[], chartData: ChartDataProps[]) => void;
  cellMinWidth?: number;
  data: { [key: string]: number | string; }[];
  dataItems: ChartDataProps[];
}

export const TableData: React.FC<TableProps> = (data) => {
  const { cellMinWidth } = data;

  const [titleError, setTitleError] = useState<string | undefined>(undefined);
  const [dados, setDados] = useState<{ [key: string]: number | string; }[]>(data.data);
  const [rows, setRows] = useState<{ [key: string]: number | string; }[]>([]);
  const [chartData, setChartData] = useState<ChartDataProps[]>(data.dataItems);
  const [rowId, setRowId] = useState<string>("id");
  const [columns, setColumns] = useState<GridColDef[]>([]);

  // Alterando dados
  const handleTitleChange = (field: string, title: string) => {
    if (title === "x") {
      setTitleError("A coluna não pode ter nome X");
      return;
    }

    const newDados = dados.map((dado) => {
      if (title === field) return dado;

      const newDados = { ...dado };
      newDados[title] = newDados[field];
      delete newDados[field];
      return newDados;
    });
    setDados(newDados);

    const chartDatas = chartData.map((data)=>{
      const newData = {...data};
      if (newData.dataKey === field) {
        newData.dataKey = title;
      }
      return newData;
    });
    setChartData(chartDatas);
  };
  const handleStyle = (cel: string, style: ChartDataProps) => {
    const chartsData = chartData.filter((data) => data.dataKey !== cel);
    chartsData.push(style);
    setChartData(chartsData);
  };

  // Adicionando dados
  const handleAddRow = () => {
    const data = [...dados, { "x": dados.length + 1 }];
    setDados(data);
  };
  const handleAddCol = () => {
    const data = dados.map((data) => {
      const newData = { ...data };
      newData["newValue"] = 0;
      delete newData["id"];
      return newData;
    });
    setDados(data);

    const newChartData = [...chartData, {
      dataKey: "newValue",
      typeGraph: "bar" as graficType,
      fill: generateRandomColor(),
    }];
    setChartData(newChartData);
  };

  // Removendo dados
  const handleRemoveRow = (id: string, newDados: { [key: string]: number | string; }[]) => {
    const data = newDados.filter((dado) => dado[rowId] !== id).map((data) => {
      const newData = { ...data };
      delete newData[rowId];
      return newData;
    });

    setDados(data);
  };
  const handleRemoveCol = (col: string) => {
    const data = dados.map((data) => {
      delete data[col];
      return data;
    });
    setDados(data);

    const chartDatas = chartData.filter((data) => data.dataKey !== col);
    setChartData(chartDatas);
  };

  // Dados iniciais caso venha uma lista vazia
  useEffect(() => {
    if (dados.length === 0) {
      setDados([
        {
          "x": 1,
          "value1": 15,
          "value2": 12,
          "value3": 1,
        },
        {
          "x": 2,
          "value1": 5,
          "value2": 10,
          "value3": 12,
        },
      ]);

      setChartData([
        {
          fill: generateRandomColor(),
          typeGraph: "bar",
          dataKey: "value1",
          stackId: "Stack1"
        },
        {
          fill: generateRandomColor(),
          typeGraph: "bar",
          dataKey: "value2",
        },
        {
          fill: generateRandomColor(),
          typeGraph: "bar",
          dataKey: "value3",
          stackId: "Stack1"
        }
      ]);
    }
  }, []);

  useEffect(() => {
    if (dados.length === 0) return;

    // Removendo a coluna ID anterior
    const data = generateTableData({ dados: dados });
    const newDados = data.dados;
    const rowIdNow = data.id;

    const columnsAdd = generateTableCols({
      dados: newDados.map((data) => {
        const newData = { ...data };
        delete newData[rowIdNow];
        return newData;
      }),
      width: cellMinWidth,
      styles: chartData,
      onRemoveCol: handleRemoveCol,
      onRemoveRow: (id) => handleRemoveRow(id, newDados),
      onAddCol: handleAddCol,
      onAddRow: handleAddRow,
      onTitleChange: handleTitleChange,
      onStyleChange: handleStyle,
    });

    // Atualizando as variáveis de estado
    setRowId(rowIdNow);
    setRows(newDados);
    setColumns(columnsAdd);
  }, [dados, chartData]);

  useEffect(() => {
    if (!data.onDataChange) return;

    const newDados = dados.map((data) => {
      const dataCopy = { ...data };
      delete dataCopy[rowId];
      return dataCopy;
    });

    data.onDataChange(newDados, chartData);
  }, [dados, chartData]);

  return <>
    <Snackbar
      open={Boolean(titleError)}
      autoHideDuration={3000}
      onClose={() => setTitleError(undefined)}
    >
      <Alert onClose={() => setTitleError(undefined)} severity="warning">
        {titleError}
      </Alert>
    </Snackbar>

    <DataGrid
      rows={rows}
      columns={columns}
      onCellEditStop={(params, event) => {
        const id = params.id;
        const value = (event as React.ChangeEvent<HTMLInputElement>)?.target?.value;
        const field = params.field;

        const data = rows.map((data) => {
          const newData = { ...data };
          if (newData[rowId] === id && value) {
            const numb = Number(value);
            newData[field] = isNaN(numb) ? value : numb;
          }
          delete newData[rowId];
          return newData;
        });
        setDados(data);
      }}
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      columnGroupingModel={generateTableGroups({ dados: chartData })}
      getRowId={(dados) => dados[rowId]}
    />
  </>;
};